import API_ENDPOINTS from '../../config/api.js';
import { proxyFetch } from '../proxyAuth.js';

const COMPANY_ATTR = 'company_id';

function currentCompanyId() {
  const el = document.getElementById('current-company-marker');
  return el ? el.value.trim() : '';
}

async function renderSections(ids) {
  const res = await fetch(`?sections=${ids.join(',')}`);
  return res.json();
}

// Re-render the mini-cart drawer (and count bubble) into its emptied state.
async function refreshDrawer() {
  const drawer =
    document.querySelector('cart-notification') ||
    document.querySelector('cart-drawer');
  if (!drawer || !drawer.getSectionsToRender) return;

  const ids = drawer.getSectionsToRender().map((s) => s.id);
  const sections = await renderSections(ids);
  drawer.renderContents({ sections });
  drawer.classList.add('is-empty');
  drawer.querySelector('.drawer__inner')?.classList.add('is-empty');
}

// Re-render the main cart page into its emptied state.
async function refreshCartPage() {
  const cartItems = document.querySelector('cart-items');
  if (!cartItems || !cartItems.getSectionsToRender) return;

  const toRender = cartItems.getSectionsToRender();
  const sections = await renderSections(toRender.map((s) => s.section));

  toRender.forEach((section) => {
    const el =
      document.getElementById(section.id)?.querySelector(section.selector) ||
      document.getElementById(section.id);
    if (el && sections[section.section]) {
      el.innerHTML = cartItems.getSectionInnerHTML(
        sections[section.section],
        section.selector,
      );
    }
  });

  cartItems.classList.add('is-empty');
  document.getElementById('main-cart-footer')?.classList.add('is-empty');
}

async function refreshCartUI() {
  await Promise.all([refreshDrawer(), refreshCartPage()]);
  window.dispatchEvent(new CustomEvent('cart:quantity-update'));
}

// Stamp the active company onto the cart so its owner travels with the line items.
export async function stampCartCompany() {
  const companyId = currentCompanyId();
  if (!companyId) return;

  await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: { [COMPANY_ATTR]: companyId } }),
  });
}

const SWITCH_FLAG = 'company-switch-inflight';

// COB2B-809: a rep picks up a role on each location they switch to. Once the
// switch has landed and the old cart is cleared, drop the rest so they're left on
// one location — otherwise Shopify shows its own location picker at the next login.
// Only now is it safe: revoking before the clear is what 422s it. Fire-and-forget —
// if it fails the rep just keeps an extra role until the next switch.
function pruneRepRoles() {
  proxyFetch(API_ENDPOINTS.SALES_REP_PRUNE_ROLES, { method: 'POST' })
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    })
    .catch((e) => console.error('[cart_company_guard] could not prune rep roles', e));
}

// On load, drive Shopify's native buyer context to the Ceres-selected location,
// then clear any residue left by the previous company — in that order. Clearing
// against a stale/unset native location 422s ("You can't purchase for this
// location"), so the native switch must happen first.
export async function reconcileCartCompany() {
  const marker = document.getElementById('location-switch-marker');
  const inflight = sessionStorage.getItem(SWITCH_FLAG);

  // No mismatch: native buyer context already points at the selected location.
  if (!marker) {
    if (!inflight) return;

    // Post-switch landing: context is now valid, clear the old company's items.
    sessionStorage.removeItem(SWITCH_FLAG);
    await fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    await refreshCartUI();
    pruneRepRoles();
    return;
  }

  // Loop breaker: we already navigated to the switch URL for this location and
  // the mismatch persists — don't redirect again.
  if (inflight === marker.dataset.targetLocationId) {
    sessionStorage.removeItem(SWITCH_FLAG);
    console.error(
      '[cart_company_guard] native location switch did not stick',
      marker.dataset.targetLocationId,
    );
    return;
  }

  sessionStorage.setItem(SWITCH_FLAG, marker.dataset.targetLocationId);
  window.location.assign(marker.value);
}
